import React, { useState, useEffect } from "react";
import { axiosInstance } from "../context/AuthContext";

const CreateNewGallery = ({ isOpen, onCancel, onSuccess, editingGallery }) => {
  const [displayName, setDisplayName] = useState("");
  const [technicalContactEmail, setTechnicalContactEmail] = useState("");
  const [technicalContactPhone, setTechnicalContactPhone] = useState("");
  const [galleryId, setGalleryId] = useState("");
  const [error, setError] = useState(null);

  // Load editing gallery data or reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen && editingGallery) {
      setDisplayName(editingGallery.displayName || "");
      setTechnicalContactEmail(editingGallery.technicalContactEmail || "");
      setTechnicalContactPhone(editingGallery.technicalContactPhone || "");
      setGalleryId(editingGallery.galleryId || "");
      setError(null);
    } else if (!isOpen) {
      setDisplayName("");
      setTechnicalContactEmail("");
      setTechnicalContactPhone("");
      setGalleryId("");
      setError(null);
    }
  }, [isOpen, editingGallery]);

  const validateForm = () => {
    if (!displayName || !technicalContactEmail || !technicalContactPhone || !galleryId) {
      setError("All fields are required");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); // Clear previous errors
  
    if (!validateForm()) {
      return;
    }
  
    const formData = {
      displayName,
      technicalContactEmail,
      technicalContactPhone,
      galleryId
    };
  
    try {
      let response;
      if (editingGallery) {
        // Update existing gallery
        response = await axiosInstance.put(`/gallery/${editingGallery._id}`, formData);
      } else {
        // Create new gallery
        response = await axiosInstance.post("/gallery", formData);
      }

      if (response.status === 200 || response.status === 201) {
        if (onSuccess) {
          onSuccess();
        } else {
          window.location.reload();
        }
        onCancel(); // Close the dialog
      } else {
        setError("Gallery operation failed");
      }
    } catch (error) {
      console.error("Error sending data to the backend", error);
      // Display error message from server or a default message
      if (error.response && error.response.data && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        setError("An error occurred while creating the gallery. Please try again.");
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay Background */}
      <div
        className="fixed top-0 left-0 w-full h-full bg-black opacity-50 z-40"
      ></div>

      {/* Dialog Content */}
      <div 
        className="fixed top-0 left-0 w-full h-full flex items-center justify-center z-50"
        onClick={onCancel}
      >
        <div 
          className="max-w-lg w-full mx-4 p-6 bg-white rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">{editingGallery ? "Edit Gallery" : "Setup New Gallery"}</h2>
            <button
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              onClick={onCancel}
            >
              ×
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="displayName" className="block text-gray-700 mb-1">
                Gallery Name:
              </label>
              <input
                type="text"
                id="displayName"
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="technicalContactEmail" className="block text-gray-700 mb-1">
                Technical Contact Email:
              </label>
              <input
                type="email"
                id="technicalContactEmail"
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                value={technicalContactEmail}
                onChange={(e) => setTechnicalContactEmail(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="technicalContactPhone" className="block text-gray-700 mb-1">
                Technical Contact Phone:
              </label>
              <input
                type="tel"
                id="technicalContactPhone"
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                value={technicalContactPhone}
                onChange={(e) => setTechnicalContactPhone(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="galleryId" className="block text-gray-700 mb-1">
                Gallery ID:
              </label>
              <input
                type="text"
                id="galleryId"
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                value={galleryId}
                onChange={(e) => setGalleryId(e.target.value)}
              />
            </div>
            {error && <p className="mb-4 text-red-500 text-sm">{error}</p>}
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none"
              >
                {editingGallery ? "Update" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateNewGallery;
